import { STATI_CHIUSI, type RichiestaFields, type Stato } from "@app/schema";
import { where, type FieldsPatch, type ListItem } from "@app/sp-data";
import type { DataServices } from "./DataProvider";
import { compressImage } from "../lib/images";
import { storage } from "../lib/storage";

/**
 * Logica applicativa delle richieste: le pagine chiamano queste funzioni,
 * non i repository direttamente.
 */

export type Richiesta = ListItem<RichiestaFields>;
const q = where<RichiestaFields>();

/* ------------------------------ Richiedente ------------------------------ */

/**
 * Richieste dell'utente corrente.
 * Per i coordinatori SharePoint restituisce già solo le proprie (permessi a livello
 * di elemento); il filtro lato client serve per chi è anche gestore e vede tutto.
 */
export async function loadMine(s: DataServices, nextLink?: string) {
  const page = nextLink
    ? await s.richieste.next(nextLink, true)
    : await s.richieste.query({ orderBy: { field: "Created", desc: true }, top: 50, allowNonIndexed: true });
  const mine = (r: Richiesta) =>
    r.createdBy?.id === s.me.id ||
    [s.me.mail, s.me.userPrincipalName].some((m) => m && r.createdBy?.email?.toLowerCase() === m.toLowerCase());
  return { items: s.roles.gestore ? page.items.filter(mine) : page.items, nextLink: page.nextLink };
}

export interface NuovaRichiesta {
  titolo: string;
  sede: string;
  servizio: string;
  /** Solo dove l'elenco "Servizi per sede" prevede aree (es. HOUSING). */
  area?: string;
  categoria: string;
  descrizione: string;
  urgente: boolean;
  foto: File[];
}

export interface InvioResult {
  id: number;
  fotoNonCaricate: number;
}

/** Crea la richiesta, imposta il richiedente e carica le foto (compresse). */
export async function inviaRichiesta(
  s: DataServices,
  r: NuovaRichiesta,
  onProgress?: (fase: string, frazione: number) => void,
): Promise<InvioResult> {
  onProgress?.("Invio della richiesta", 0);
  const cachedLookup = myLookupId.get(s);
  const item = await s.richieste.create({
    Title: r.titolo.trim(),
    Sede: r.sede,
    Servizio: r.servizio,
    Area: r.area || undefined,
    Categoria: r.categoria,
    Descrizione: r.descrizione.trim(),
    Priorita: r.urgente ? "Urgente" : "Standard",
    Stato: "Sottomessa",
    Richiedente: cachedLookup,
  });

  // Prima richiesta dell'utente: il LookupId si ricava dall'autore appena registrato
  if (!cachedLookup && item.authorLookupId) {
    myLookupId.set(s, item.authorLookupId);
    try {
      await s.richieste.update(item.id, { Richiedente: item.authorLookupId });
    } catch {
      /* non bloccante: "Creato da" contiene comunque l'autore */
    }
  }

  ricordaScelte(s, { sede: r.sede, servizio: r.servizio });

  let fotoNonCaricate = 0;
  for (const [i, file] of r.foto.entries()) {
    onProgress?.(`Caricamento foto ${i + 1} di ${r.foto.length}`, i / r.foto.length);
    try {
      const blob = await compressImage(file);
      const nome = `${Date.now()}-${i + 1}.jpg`;
      await s.foto.upload(`${item.id}/${nome}`, blob, { fields: { RichiestaId: item.id } });
    } catch (e) {
      console.warn("Foto non caricata", e);
      fotoNonCaricate++;
    }
  }
  onProgress?.("Fatto", 1);
  return { id: item.id, fotoNonCaricate };
}

/* ------------------------------- Gestore --------------------------------- */

export const FILTRI_CODA = {
  "Da gestire": ["Sottomessa", "Presa visione"],
  "In corso": ["In lavorazione", "Bloccata"],
  Chiuse: ["Evasa", "Respinta"],
  Tutte: [],
} as const satisfies Record<string, readonly Stato[]>;
export type FiltroCoda = keyof typeof FILTRI_CODA;

/** Filtri della coda del gestore: stato e, facoltativa, sede (colonne indicizzate). */
export async function loadCoda(s: DataServices, filtro: FiltroCoda, sede?: string, nextLink?: string) {
  const stati = FILTRI_CODA[filtro] as readonly Stato[];
  const parti = [
    stati.length ? q.or(...stati.map((st) => q.eq("Stato", st))) : undefined,
    sede ? q.eq("Sede", sede) : undefined,
  ].filter((x): x is string => !!x);
  const page = nextLink
    ? await s.richieste.next(nextLink, true)
    : await s.richieste.query({
        filter: parti.length > 1 ? q.and(...parti) : parti[0],
        orderBy: { field: "Created", desc: true },
        top: 50,
        allowNonIndexed: true,
      });
  return page;
}

/** Urgenti in cima, poi dalla più recente. */
export function ordinaCoda(items: Richiesta[]): Richiesta[] {
  return [...items].sort((a, b) => {
    const ua = a.fields.Priorita === "Urgente" ? 0 : 1;
    const ub = b.fields.Priorita === "Urgente" ? 0 : 1;
    return ua - ub || b.id - a.id;
  });
}

export interface Gestione {
  stato: Stato;
  quantita?: number;
  valore?: number;
  extraBudget: boolean;
  noteGestore: string;
  /** Id nell'elenco Fornitori manutenzione; undefined = nessuno. */
  fornitore?: number;
}

/**
 * Salva le modifiche del gestore. Imposta automaticamente DataChiusura
 * (stati finali) e GestitoDa. Con l'ETag, se qualcun altro ha modificato
 * la richiesta nel frattempo, Graph risponde 412 (GraphError.isConflict).
 */
export async function salvaGestione(s: DataServices, r: Richiesta, g: Gestione): Promise<void> {
  const patch: FieldsPatch<RichiestaFields> = {
    Stato: g.stato,
    Quantita: g.quantita ?? null,
    Valore: g.valore ?? null,
    ExtraBudget: g.extraBudget,
    NoteGestore: g.noteGestore.trim() || null,
  };
  // Solo se cambia: chi apre una richiesta senza toccare il fornitore non lo azzera
  if ((g.fornitore ?? null) !== (r.fields.Fornitore ?? null)) patch.Fornitore = g.fornitore ?? null;

  const eraChiusa = STATI_CHIUSI.includes(r.fields.Stato as Stato);
  const saraChiusa = STATI_CHIUSI.includes(g.stato);
  if (saraChiusa && !eraChiusa) patch.DataChiusura = new Date().toISOString();
  if (!saraChiusa && eraChiusa) patch.DataChiusura = null;

  const statoCambiato = g.stato !== r.fields.Stato;
  let lookup = statoCambiato ? await resolveMyLookupId(s) : undefined;
  if (lookup) patch.GestitoDa = lookup;

  const res = await s.richieste.update(r.id, patch, r.etag);

  // LookupId non risolvibile prima: si ricava dall'editor appena registrato
  if (statoCambiato && !lookup && res.EditorLookupId != null) {
    lookup = Number(res.EditorLookupId);
    myLookupId.set(s, lookup);
    await s.richieste.update(r.id, { GestitoDa: lookup });
  }
}

async function resolveMyLookupId(s: DataServices): Promise<number | undefined> {
  const cached = myLookupId.get(s);
  if (cached) return cached;
  for (const email of [s.me.mail, s.me.userPrincipalName]) {
    if (!email) continue;
    try {
      const u = await s.users.byEmail(email);
      if (u) {
        myLookupId.set(s, u.lookupId);
        return u.lookupId;
      }
    } catch {
      /* lista utenti non leggibile: si ripiega sull'editor */
    }
  }
  return undefined;
}

/* --------------------------- Preferenze locali --------------------------- */

const myLookupId = {
  key: (s: DataServices) => `lookup:${s.site.id}:${s.me.id}`,
  get: (s: DataServices) => {
    const v = storage.get(myLookupId.key(s));
    return v ? Number(v) : undefined;
  },
  set: (s: DataServices, id: number) => storage.set(myLookupId.key(s), String(id)),
};

/** Ultime scelte del richiedente, per precompilare il modulo successivo. */
export interface SceltePrecedenti {
  sede?: string;
  servizio?: string;
}

export function sceltePrecedenti(s: DataServices): SceltePrecedenti {
  try {
    const v = JSON.parse(storage.get(`scelte:${s.me.id}`) ?? "") as SceltePrecedenti;
    return { sede: v.sede, servizio: v.servizio };
  } catch {
    return {};
  }
}

function ricordaScelte(s: DataServices, scelte: SceltePrecedenti) {
  storage.set(`scelte:${s.me.id}`, JSON.stringify(scelte));
}
